import { services } from '@config/services';
import { inventoryApi } from './base';
import type {
  BulkUploadResponse,
  BulkUploadRow,
  InventoryFacilityProductsResponse,
  InventorySearchResponse,
  VendorSkuLocation,
} from './types';

/** Args for the facility-grouped vendor-products query (table rows). */
export interface FacilityProductsArgs {
  /** Selected vendor filter; omitted from the URL entirely when empty (all vendors). */
  vendorId?: string;
  /** Facility filter, sent as repeated `facilityIds` params (union server-side). */
  facilityIds?: string[];
  /** SKU search string; a 1-char value is handled by the hook's `skip`, not here. */
  searchString?: string | null;
  page: number;
  pageSize: number;
}

/** Args for the admin inventory search. */
export interface SearchInventoryArgs {
  page: number;
  pageSize: number;
  vendorSkuLocationList: VendorSkuLocation[];
}

/** Args for the vendor-portal inventory search (adds the scoping vendorId). */
export interface SearchVendorInventoryArgs extends SearchInventoryArgs {
  vendorId: string;
}

/**
 * Inventory endpoints.
 *
 * Wire shapes pasted from the LIVE legacy capture (DevTools, 2026-05-30) —
 * see docs/migration/inventory/link-graph.md §11. `transformResponse` unwraps
 * each envelope explicitly off the RAW body (the legacy `{ response }` wrapper
 * was a Redux artifact, NOT on the wire).
 */
export const inventoryEndpoints = inventoryApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Facility-grouped vendor products that seed the table rows.
     *
     * LIVE: GET /vendor/v1/vendorProducts?&page=1&pageSize=50&sortAsc=false&showBy=facility
     *   (all-vendors omits `vendorId`; selecting a vendor adds `vendorId={id}`;
     *    a SKU search adds `searchAll={q}`; a facility filter adds one
     *    `facilityIds={id}` per selected facility). Response is a PRE-FLATTENED
     *    `{ vendorFacilityProducts: [...], total }` (NOT the nested
     *    `{ vendorProducts: [{ facilities }] }` the vendor feature's default
     *    view returns) — which is why this query is inventory-owned rather than
     *    reusing `useGetVendorProductsQuery`.
     *
     * A `facilityIds` filter narrows to products stocked at ANY of them, but the
     * response still carries each match's other facility rows for the hook to prune.
     */
    getInventoryFacilityProducts: builder.query<
      InventoryFacilityProductsResponse,
      FacilityProductsArgs
    >({
      query: ({ vendorId, facilityIds, searchString, page, pageSize }) => {
        const qs = new URLSearchParams();
        if (vendorId) qs.set('vendorId', vendorId);
        for (const id of facilityIds ?? []) qs.append('facilityIds', id);
        if (searchString) qs.set('searchAll', searchString);
        qs.set('page', String(page));
        qs.set('pageSize', String(pageSize));
        // Legacy passes no sortBy/sortDir → service emits sortAsc=false.
        qs.set('sortAsc', 'false');
        qs.set('showBy', 'facility');
        return { url: `${services.vendor}/v1/vendorProducts?${qs.toString()}`, method: 'GET' };
      },
      transformResponse: (
        response: InventoryFacilityProductsResponse,
      ): InventoryFacilityProductsResponse => ({
        vendorFacilityProducts: response?.vendorFacilityProducts ?? [],
        total: response?.total ?? 0,
      }),
      providesTags: [{ type: 'InventoryProducts', id: 'LIST' }],
    }),

    /**
     * Stock lookup for the current page's SKU/location list (admin / non-vendor).
     *
     * LIVE: POST /inventory/v1/search
     *   body { page, pageSize, search: { vendorSkuLocationList } }
     *   → { inventories: [...], total }
     */
    searchInventory: builder.query<InventorySearchResponse, SearchInventoryArgs>({
      query: ({ page, pageSize, vendorSkuLocationList }) => ({
        url: `${services.inventory}/v1/search`,
        method: 'POST',
        body: { page, pageSize, search: { vendorSkuLocationList } },
      }),
      transformResponse: (response: InventorySearchResponse): InventorySearchResponse => ({
        inventories: response?.inventories ?? [],
        total: response?.total ?? 0,
      }),
      providesTags: [{ type: 'Inventory', id: 'LIST' }],
    }),

    /**
     * Stock lookup for a vendor persona (omVendorPortalEnabled ON, live=true).
     *
     * LIVE: POST /inventory/v1/vendor/search
     *   body { page, pageSize, search: { vendorId, vendorSkuLocationList } }
     *   → { inventories: [...], total }
     */
    searchVendorInventory: builder.query<InventorySearchResponse, SearchVendorInventoryArgs>({
      query: ({ page, pageSize, vendorId, vendorSkuLocationList }) => ({
        url: `${services.inventory}/v1/vendor/search`,
        method: 'POST',
        body: { page, pageSize, search: { vendorId, vendorSkuLocationList } },
      }),
      transformResponse: (response: InventorySearchResponse): InventorySearchResponse => ({
        inventories: response?.inventories ?? [],
        total: response?.total ?? 0,
      }),
      providesTags: [{ type: 'Inventory', id: 'LIST' }],
    }),

    /**
     * Apply inline edits and CSV uploads. Request body is a RAW ARRAY.
     *
     * LIVE: POST /inventory/v1/vendor/bulk/upload
     *   body [{ vendorId, vendorFacilityId, sku, vendorSku, quantity, status, isInfinite }]
     *   → { bulkUploadItemsResponse: [{ isSuccess, errorMessage? }] }
     *
     * No `invalidatesTags` — legacy does not refetch after an edit (the page's
     * local working copy is authoritative; see base.ts + link-graph §12).
     */
    bulkUploadInventory: builder.mutation<BulkUploadResponse, BulkUploadRow[]>({
      query: (rows) => ({
        url: `${services.inventory}/v1/vendor/bulk/upload`,
        method: 'POST',
        body: rows,
      }),
      transformResponse: (response: BulkUploadResponse): BulkUploadResponse => ({
        bulkUploadItemsResponse: response?.bulkUploadItemsResponse ?? [],
      }),
    }),
  }),
});

export const {
  useGetInventoryFacilityProductsQuery,
  useSearchInventoryQuery,
  useSearchVendorInventoryQuery,
  useBulkUploadInventoryMutation,
} = inventoryEndpoints;
